import React, { useState, useCallback } from 'react';

export function ConfirmModal({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  onConfirm,
  onClose,
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/40 flex items-end md:items-center justify-center z-[60] md:px-4" onClick={onClose}>
      <div className="bg-white rounded-t-[28px] md:rounded-xl p-6 pb-[calc(24px+env(safe-area-inset-bottom))] md:pb-6 w-full md:max-w-sm" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-bold text-text mb-2">{title}</h3>
        {message && <p className="text-sm text-gray-600 mb-6">{message}</p>}
        <div className="flex justify-end gap-3 [&>button]:flex-1 [&>button]:h-12 md:[&>button]:flex-none md:[&>button]:h-auto">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            className={
              destructive
                ? 'px-4 py-2 text-sm font-medium text-white bg-destructive rounded-lg hover:opacity-90'
                : 'px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-80'
            }
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * useConfirm — Promise-based replacement for window.confirm.
 * Usage:
 *   const { confirm, confirmModal } = useConfirm();
 *   if (!(await confirm({ title: 'Delete?', message: '...', destructive: true }))) return;
 *   // ... do the action
 *   return (<>{...page}{confirmModal}</>)
 */
export function useConfirm() {
  const [state, setState] = useState(null);

  const confirm = useCallback((opts) => {
    return new Promise((resolve) => {
      setState({ ...opts, resolve });
    });
  }, []);

  const handleConfirm = () => {
    if (state) state.resolve(true);
    setState(null);
  };
  const handleCancel = () => {
    if (state) state.resolve(false);
    setState(null);
  };

  const confirmModal = (
    <ConfirmModal
      open={!!state}
      title={state?.title}
      message={state?.message}
      confirmLabel={state?.confirmLabel}
      cancelLabel={state?.cancelLabel}
      destructive={state?.destructive}
      onConfirm={handleConfirm}
      onClose={handleCancel}
    />
  );

  return { confirm, confirmModal };
}
